import { useEffect, useRef } from "react";
import { io, type Socket } from "socket.io-client";
import { create } from "zustand";
import { env } from "@/config/envvars";
import { useAuthStore } from "@/stores/authStore";
import {
  SOCKET_UNAUTHORIZED,
  type BrandSubscribeResult,
  type RealtimeClientEvents,
  type RealtimeEnvelope,
  type RealtimeServerEvents,
  type WorkshopLiveFrame,
  type WorkshopSubscribeResult,
  type WorkshopThreadJoinResult,
} from "@/types/realtime.type";
import { refreshAccessToken } from "./api";
import { isTokenFresh } from "./auth";
import { CLIENT_ID } from "./clientId";
import { POLL_SOCKET_SAFETY_MS } from "./realtime";

/**
 * The realtime connection: one socket per tab, owned by RealtimeProvider.
 * Pages never open their own. They read `useIsBrandLive` to relax polling and
 * `useRealtimeEvent` to react to what teammates do. Cache updates happen
 * centrally, in dashboard/api/realtimeHandlers.ts.
 */

export type RealtimeSocket = Socket<RealtimeServerEvents, RealtimeClientEvents>;

/** A token this close to expiry is refreshed before it is handed to the handshake. */
const TOKEN_MARGIN_MS = 60_000;
/** Backoff for handshakes the server refused over the token. */
const AUTH_RETRY_BASE_MS = 2_000;
const AUTH_RETRY_MAX_MS = 60_000;
/** After this many refusals in a row the tab stays on polling until reload or re-login. */
const MAX_AUTH_FAILURES = 5;
/** The server closes every tab's socket at token expiry; this spreads their reconnects. */
const EXPIRY_RECONNECT_JITTER_MS = 2_000;
const SUBSCRIBE_TIMEOUT_MS = 10_000;
const SUBSCRIBE_RETRY_MS = 5_000;

type RealtimeStatus = "offline" | "connecting" | "online";

interface RealtimeState {
  socket: RealtimeSocket | null;
  status: RealtimeStatus;
  /** The brand whose room this tab is in, meaning its changes are being pushed here. */
  liveBrandId: string | null;
}

export const useRealtimeStore = create<RealtimeState>(() => ({
  socket: null,
  status: "offline",
  liveBrandId: null,
}));

const setState = useRealtimeStore.setState;

/** True while this brand's changes are pushed to this tab. */
export function useIsBrandLive(brandId: string | null): boolean {
  return useRealtimeStore(
    (s) => !!brandId && s.status === "online" && s.liveBrandId === brandId,
  );
}

/**
 * refetchInterval for a query whose changes arrive over the socket. While the
 * brand is live, polling drops to a slow safety net for writes nothing
 * announces. Otherwise it falls back to the query's normal cadence.
 */
export function useLiveRefetchInterval(brandId: string | null, fallbackMs: number): number {
  return useIsBrandLive(brandId) ? POLL_SOCKET_SAFETY_MS : fallbackMs;
}

// --- Event fan-out -----------------------------------------------------------

type Listener = (event: RealtimeEnvelope) => void;
const listeners = new Set<Listener>();

export function dispatchRealtimeEvent(event: RealtimeEnvelope) {
  for (const listener of listeners) {
    try {
      listener(event);
    } catch (err) {
      console.error("[realtime] listener failed:", err);
    }
  }
}

/** Calls `listener` with every realtime event while the component is mounted. */
export function useRealtimeEvent(listener: Listener) {
  const latest = useRef(listener);
  useEffect(() => {
    latest.current = listener;
  });
  useEffect(() => {
    const forward: Listener = (event) => latest.current(event);
    listeners.add(forward);
    return () => {
      listeners.delete(forward);
    };
  }, []);
}

// --- Connection --------------------------------------------------------------

async function handshakeToken(): Promise<string | null> {
  const { token } = useAuthStore.getState();
  if (!token || isTokenFresh(token, TOKEN_MARGIN_MS)) return token;
  try {
    return await refreshAccessToken();
  } catch {
    // The server refuses it and connect_error takes over from there.
    return useAuthStore.getState().token;
  }
}

/** Opens the tab's socket and publishes it on the store. Returns a closer. */
export function openRealtimeConnection(): () => void {
  const socket: RealtimeSocket = io(env.BACKEND_URL, {
    // WebSocket only. Long-polling needs sticky sessions, which the ALB does
    // not have, so it would break as soon as the API runs on more than one task.
    transports: ["websocket"],
    forceNew: true,
    reconnectionDelayMax: 10_000,
    // Called on every (re)connect, so a reconnect always sends the current token.
    auth: (cb) => {
      void handshakeToken().then((token) => cb({ token, clientId: CLIENT_ID }));
    },
  });

  let authFailures = 0;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  const reconnectIn = (ms: number) => {
    clearTimeout(retryTimer);
    retryTimer = setTimeout(() => socket.connect(), ms);
  };

  socket.on("connect", () => {
    authFailures = 0;
    setState({ status: "online" });
  });

  socket.on("disconnect", (reason) => {
    setState({ status: "connecting", liveBrandId: null });
    // The server closes a socket when its token expires and leaves the
    // reconnect to us. Transport drops are retried by the manager itself.
    if (reason === "io server disconnect") {
      reconnectIn(Math.random() * EXPIRY_RECONNECT_JITTER_MS);
    }
  });

  socket.on("connect_error", (err) => {
    setState({ liveBrandId: null });
    // Unreachable server: the manager keeps retrying with its own backoff.
    if (err.message !== SOCKET_UNAUTHORIZED) {
      setState({ status: "connecting" });
      return;
    }
    // A refused token is not retried automatically. Refresh it and try again,
    // but give up rather than rotate tokens forever: polling still works.
    authFailures += 1;
    if (authFailures > MAX_AUTH_FAILURES) {
      setState({ status: "offline" });
      return;
    }
    setState({ status: "connecting" });
    const delay = Math.min(AUTH_RETRY_BASE_MS * 2 ** (authFailures - 1), AUTH_RETRY_MAX_MS);
    // A 440 from the refresh ends the session through the axios interceptor.
    refreshAccessToken()
      .catch(() => undefined)
      .finally(() => reconnectIn(delay));
  });

  // A socket that gave up over its token gets another chance once the token
  // rotates (a 401 refresh or a login in this tab).
  const unsubscribeAuth = useAuthStore.subscribe((state, prev) => {
    if (state.token === prev.token || !state.token) return;
    if (authFailures <= MAX_AUTH_FAILURES) return;
    authFailures = 0;
    socket.connect();
  });

  setState({ socket, status: "connecting", liveBrandId: null });

  return () => {
    clearTimeout(retryTimer);
    unsubscribeAuth();
    socket.removeAllListeners();
    socket.disconnect();
    if (useRealtimeStore.getState().socket === socket) {
      setState({ socket: null, status: "offline", liveBrandId: null });
    }
  };
}

/** How many active subscriptions this tab holds per brand. */
const brandSubscriptions = new Map<string, number>();

function releaseBrand(socket: RealtimeSocket, brandId: string) {
  if ((brandSubscriptions.get(brandId) ?? 0) > 0) return;
  if (socket.connected) socket.emit("brand:unsubscribe", brandId);
}

/**
 * Keeps this tab in the brand's room, rejoining after every reconnect, until
 * the returned cleanup runs. `onLive` fires each time the join is confirmed,
 * with the time it was, so the caller can refetch whatever it may have missed.
 * A refused join (no access) leaves the brand on polling.
 */
export function subscribeToBrand(
  socket: RealtimeSocket,
  brandId: string,
  onLive: (since: number) => void,
): () => void {
  let active = true;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  brandSubscriptions.set(brandId, (brandSubscriptions.get(brandId) ?? 0) + 1);

  const join = async () => {
    if (!active || !socket.connected) return;
    let result: BrandSubscribeResult;
    try {
      result = await socket.timeout(SUBSCRIBE_TIMEOUT_MS).emitWithAck("brand:subscribe", brandId);
    } catch {
      result = { ok: false, error: "error" };
    }
    if (!active) {
      // The join landed after this subscription ended; leave again unless a
      // newer subscription for the same brand still wants the room.
      if (result.ok) releaseBrand(socket, brandId);
      return;
    }
    if (result.ok) {
      setState({ liveBrandId: brandId });
      onLive(Date.now());
    } else if (result.error === "error" && socket.connected) {
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => void join(), SUBSCRIBE_RETRY_MS);
    }
  };

  const onConnect = () => void join();
  socket.on("connect", onConnect);
  void join();

  return () => {
    active = false;
    clearTimeout(retryTimer);
    socket.off("connect", onConnect);
    brandSubscriptions.set(brandId, (brandSubscriptions.get(brandId) ?? 1) - 1);
    releaseBrand(socket, brandId);
    if (useRealtimeStore.getState().liveBrandId === brandId) {
      setState({ liveBrandId: null });
    }
  };
}

// --- Workshop rooms ------------------------------------------------------------
//
// A workshop project has its own room (its data hints), and a shared thread has
// one too: joining it is what makes the viewer present there, and it carries
// the thread's live signals (the reply as it streams, typing, presence).

type WorkshopLiveListener = (frame: WorkshopLiveFrame) => void;
const workshopLiveListeners = new Set<WorkshopLiveListener>();

export function dispatchWorkshopLiveFrame(frame: WorkshopLiveFrame) {
  for (const listener of workshopLiveListeners) {
    try {
      listener(frame);
    } catch (err) {
      console.error("[realtime] workshop listener failed:", err);
    }
  }
}

/** Calls `listener` with every workshop live frame while the component is mounted. */
export function useWorkshopLiveFrame(listener: WorkshopLiveListener) {
  const latest = useRef(listener);
  useEffect(() => {
    latest.current = listener;
  });
  useEffect(() => {
    const forward: WorkshopLiveListener = (frame) => latest.current(frame);
    workshopLiveListeners.add(forward);
    return () => {
      workshopLiveListeners.delete(forward);
    };
  }, []);
}

/**
 * How many live subscriptions this tab holds per workshop room. A room is left
 * only when the last one ends. Without the count, a join whose ack lands after
 * its own cleanup (StrictMode mounts every effect twice) would leave the room
 * that the remount has just joined, and the tab would silently stop receiving
 * it.
 */
const workshopProjectSubscriptions = new Map<string, number>();
const workshopThreadSubscriptions = new Map<string, number>();

const retain = (counts: Map<string, number>, id: string) =>
  counts.set(id, (counts.get(id) ?? 0) + 1);

/** Drops one subscription; true when it was the last, and the room can be left. */
const release = (counts: Map<string, number>, id: string): boolean => {
  const next = (counts.get(id) ?? 1) - 1;
  if (next > 0) counts.set(id, next);
  else counts.delete(id);
  return next <= 0;
};

const isRetained = (counts: Map<string, number>, id: string) => (counts.get(id) ?? 0) > 0;

/**
 * Keeps this tab in a workshop project's room, rejoining after every
 * reconnect, until the cleanup runs. `onLive` fires on each confirmed join so
 * the caller can refetch what it may have missed.
 */
export function subscribeToWorkshopProject(
  socket: RealtimeSocket,
  projectId: string,
  onLive: (since: number) => void,
): () => void {
  let active = true;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  retain(workshopProjectSubscriptions, projectId);

  const join = async () => {
    if (!active || !socket.connected) return;
    let result: WorkshopSubscribeResult;
    try {
      result = await socket
        .timeout(SUBSCRIBE_TIMEOUT_MS)
        .emitWithAck("workshop:project:subscribe", projectId);
    } catch {
      result = { ok: false, error: "error" };
    }
    if (!active) {
      // Late ack of an ended subscription: leave only if nothing else wants the room.
      if (result.ok && socket.connected && !isRetained(workshopProjectSubscriptions, projectId)) {
        socket.emit("workshop:project:unsubscribe", projectId);
      }
      return;
    }
    if (result.ok) onLive(Date.now());
    else if (result.error === "error" && socket.connected) {
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => void join(), SUBSCRIBE_RETRY_MS);
    }
  };

  const onConnect = () => void join();
  socket.on("connect", onConnect);
  void join();

  return () => {
    active = false;
    clearTimeout(retryTimer);
    socket.off("connect", onConnect);
    if (release(workshopProjectSubscriptions, projectId) && socket.connected) {
      socket.emit("workshop:project:unsubscribe", projectId);
    }
  };
}

const visibilityState = (): "active" | "away" =>
  typeof document !== "undefined" && document.visibilityState === "hidden" ? "away" : "active";

/**
 * Joins a shared thread's room — the viewer is present there from now on —
 * and keeps the server told whether the tab is visible. Rejoins after every
 * reconnect; `onJoined` gets the presence and the AI's state at each join, so
 * the caller can refetch what it missed while away.
 */
export function joinWorkshopThread(
  socket: RealtimeSocket,
  sessionId: string,
  onJoined: (result: Extract<WorkshopThreadJoinResult, { ok: true }>) => void,
): () => void {
  let active = true;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  retain(workshopThreadSubscriptions, sessionId);

  const join = async () => {
    if (!active || !socket.connected) return;
    let result: WorkshopThreadJoinResult;
    try {
      result = await socket
        .timeout(SUBSCRIBE_TIMEOUT_MS)
        .emitWithAck("workshop:thread:join", { sessionId, state: visibilityState() });
    } catch {
      result = { ok: false, error: "error" };
    }
    if (!active) {
      // Late ack of an ended subscription: leave only if nothing else wants the room.
      if (result.ok && socket.connected && !isRetained(workshopThreadSubscriptions, sessionId)) {
        socket.emit("workshop:thread:leave", sessionId);
      }
      return;
    }
    if (result.ok) onJoined(result);
    else if (result.error === "error" && socket.connected) {
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => void join(), SUBSCRIBE_RETRY_MS);
    }
  };

  const onVisibility = () => {
    if (socket.connected) {
      socket.emit("workshop:thread:presence", { sessionId, state: visibilityState() });
    }
  };

  const onConnect = () => void join();
  socket.on("connect", onConnect);
  document.addEventListener("visibilitychange", onVisibility);
  void join();

  return () => {
    active = false;
    clearTimeout(retryTimer);
    socket.off("connect", onConnect);
    document.removeEventListener("visibilitychange", onVisibility);
    if (release(workshopThreadSubscriptions, sessionId) && socket.connected) {
      socket.emit("workshop:thread:leave", sessionId);
    }
  };
}

/** Tells the others in a shared thread whether this person is typing. */
export function emitWorkshopTyping(sessionId: string, typing: boolean) {
  const socket = useRealtimeStore.getState().socket;
  if (socket?.connected) socket.emit("workshop:thread:typing", { sessionId, typing });
}
